import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiOutlineCalendar,
  HiOutlineUser,
  HiOutlineClock,
  HiOutlineHeart,
  HiOutlineBookOpen,
  HiOutlineEye,
  HiOutlineSparkles,
  HiOutlineNewspaper,
  HiOutlineOfficeBuilding,
  HiOutlineX,
  HiOutlineShare,
  HiOutlinePrinter,
  HiOutlineBookmark,
  HiOutlineChevronLeft,
  HiOutlineChevronRight
} from "react-icons/hi";
import Cookies from "js-cookie";

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleBlogs, setVisibleBlogs] = useState(6);
  
  // Modal states
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [relatedBlogs, setRelatedBlogs] = useState([]);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const token = Cookies.get("userToken");
        if (!token) {
          setError("User not authenticated");
          return;
        }

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/apis/user/getAllBlog`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();

        if (!response.ok) {
          setError(data?.message || "Failed to fetch blogs");
          return;
        }

        setBlogs(data?.data || []);
      } catch (error) {
        setError("Something went wrong while fetching blogs.");
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  // Update related blogs when a blog is selected
  useEffect(() => {
    if (selectedBlog) {
      const related = blogs
        .filter(blog => 
          blog._id !== selectedBlog._id && 
          (blog.category === selectedBlog.category || 
           blog.adminId?.collegeName === selectedBlog.adminId?.collegeName)
        )
        .slice(0, 3);
      setRelatedBlogs(related);
    }
  }, [selectedBlog, blogs]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const heroVariants = {
    hidden: { opacity: 0, scale: 1.2 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 1.2, ease: "easeOut" }
    }
  };

  const textVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  // Modal animation variants
  const modalOverlayVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.3 }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.3 }
    }
  };

  const modalContentVariants = {
    hidden: { 
      opacity: 0,
      scale: 0.8,
      y: 50
    },
    visible: { 
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { 
        type: "spring",
        damping: 25,
        stiffness: 300,
        duration: 0.5
      }
    },
    exit: { 
      opacity: 0,
      scale: 0.8,
      y: 50,
      transition: { duration: 0.3 }
    }
  };

  const SkeletonLoader = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.1 }}
          className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200"
        >
          <div className="relative h-48 bg-gradient-to-r from-gray-200 to-gray-300 animate-pulse" />
          <div className="p-6 space-y-4">
            <div className="flex items-center space-x-2">
              <div className="h-4 w-20 bg-gray-200 rounded-full animate-pulse" />
              <div className="h-4 w-4 bg-gray-200 rounded-full animate-pulse" />
              <div className="h-4 w-24 bg-gray-200 rounded-full animate-pulse" />
            </div>
            <div className="h-6 bg-gray-200 rounded-lg animate-pulse w-3/4" />
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded animate-pulse w-full" />
              <div className="h-4 bg-gray-200 rounded animate-pulse w-5/6" />
              <div className="h-4 bg-gray-200 rounded animate-pulse w-4/6" />
            </div>
            <div className="h-10 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg animate-pulse w-full" />
          </div>
        </motion.div>
      ))}
    </div>
  );

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getReadingTime = (text) => {
    const wordsPerMinute = 200;
    const wordCount = text.split(/\s+/).length;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return `${minutes} min read`;
  };

  // Handle blog click to open modal
  const handleReadMore = (blog, e) => {
    e.preventDefault();
    setSelectedBlog(blog);
    setIsModalOpen(true);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedBlog(null);
    document.body.style.overflow = 'unset';
  };

  // Filter blogs based on search and category
  const filteredBlogs = blogs.filter(blog => {
    const collegeName = blog.adminId?.collegeName || "";
    const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.article.toLowerCase().includes(searchTerm.toLowerCase()) ||
      collegeName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "all" || blog.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const displayedBlogs = filteredBlogs.slice(0, visibleBlogs);
  const hasMore = visibleBlogs < filteredBlogs.length;

  const loadMore = () => {
    setVisibleBlogs(prev => Math.min(prev + 3, filteredBlogs.length));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="w-full min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden"
    >
      {/* Animated Background Elements - Light Gray */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-40"
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-40"
          animate={{
            x: [0, -50, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />

        {/* Floating icons - Light Gray */}
        <motion.div
          className="absolute top-1/4 left-1/4 text-gray-200/30 hidden lg:block"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 10, -10, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <HiOutlineNewspaper size={60} />
        </motion.div>

        <motion.div
          className="absolute bottom-1/4 right-1/4 text-gray-200/30 hidden lg:block"
          animate={{
            y: [0, 20, 0],
            rotate: [0, -10, 10, 0]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <HiOutlineBookOpen size={70} />
        </motion.div>
      </div>

      {/* Hero Section - Light Gray Overlay */}
      <div className="relative w-full h-[50vh] sm:h-[55vh] md:h-[60vh] lg:h-[70vh] overflow-hidden">
        <motion.img
          variants={heroVariants}
          initial="hidden"
          animate="visible"
          src="assets/Committee.png"
          alt="Blog Background"
          className="w-full h-full object-cover object-center"
        />

        {/* Light Gradient Overlay - Gray */}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-200/70 via-gray-100/50 to-transparent" />

        {/* Floating Elements - Light */}
        <motion.div
          className="absolute top-20 left-10 text-gray-300/40 hidden lg:block"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 10, -10, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <HiOutlineSparkles size={60} />
        </motion.div>

        {/* Hero Content - Dark text for contrast */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 sm:px-6">
          <motion.h2
            variants={textVariants}
            initial="hidden"
            animate="visible"
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4 text-gray-800"
          >
            📖 Latest Blogs
          </motion.h2>
          <motion.p
            variants={textVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.2 }}
            className="text-sm sm:text-base md:text-lg lg:text-xl font-medium text-gray-700 max-w-2xl px-4 leading-relaxed"
          >
            Stay updated with insights, trends, and stories from various fields
          </motion.p>

          {/* Stats Counter - Light Gray */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex items-center space-x-4 mt-6 sm:mt-8 bg-white/40 backdrop-blur-sm rounded-full px-4 sm:px-6 py-2 sm:py-3 border border-gray-200/60 shadow-sm"
          >
            <div className="flex items-center space-x-2">
              <HiOutlineNewspaper className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              <span className="text-gray-600 text-xs sm:text-sm font-medium">
                {blogs.length} Articles
              </span>
            </div>
            <div className="w-px h-4 bg-gray-200" />
            <div className="flex items-center space-x-2">
              <HiOutlineBookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              <span className="text-gray-600 text-xs sm:text-sm font-medium">
                Weekly Updates
              </span>
            </div>
          </motion.div>

          {/* Scroll Indicator - Light Gray */}
          <motion.div
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="w-6 h-10 border-2 border-gray-300 rounded-full flex justify-center">
              <div className="w-1 h-2 bg-gray-300 rounded-full mt-2" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-4 md:p-6 mb-8 border border-gray-200"
        >
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search Bar */}
            <div className="w-full md:w-96 relative">
              <input
                type="text"
                placeholder="Search blogs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-10 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 text-gray-700 placeholder-gray-400"
              />
              <svg
                className="absolute left-3 top-3.5 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            {/* Category Filter - Light Gray */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory("all")}
                className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
                  selectedCategory === "all"
                    ? "bg-gray-700 text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200"
                }`}
              >
                All
              </button>
              <button
                onClick={() => setSelectedCategory("technology")}
                className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
                  selectedCategory === "technology"
                    ? "bg-gray-700 text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200"
                }`}
              >
                Technology
              </button>
              <button
                onClick={() => setSelectedCategory("career")}
                className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
                  selectedCategory === "career"
                    ? "bg-gray-700 text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200"
                }`}
              >
                Career
              </button>
              <button
                onClick={() => setSelectedCategory("alumni")}
                className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
                  selectedCategory === "alumni"
                    ? "bg-gray-700 text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200"
                }`}
              >
                Alumni
              </button>
            </div>
          </div>
        </motion.div>

        {/* Blog Posts */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="min-h-[50vh]"
        >
          {loading ? (
            <SkeletonLoader />
          ) : error ? (
            <motion.div
              variants={itemVariants}
              className="text-center p-8 bg-red-50 border-2 border-red-200 rounded-2xl max-w-md mx-auto"
            >
              <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <p className="text-red-600 text-lg font-semibold">{error}</p>
            </motion.div>
          ) : filteredBlogs.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {displayedBlogs.map((blog, index) => {
                  const collegeName = blog.adminId?.collegeName || "Unknown College";
                  
                  return (
                    <motion.div
                      key={blog._id}
                      variants={itemVariants}
                      custom={index}
                      whileHover={{ y: -8 }}
                      transition={{ duration: 0.3 }}
                      className="group"
                    >
                      <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200 h-full flex flex-col hover:shadow-xl transition-shadow duration-300">
                        {/* Image Container */}
                        <div className="relative h-48 overflow-hidden">
                          <motion.img
                            src={blog.image || "assets/default-blog.png"}
                            alt={blog.title}
                            className="w-full h-full object-cover"
                            whileHover={{ scale: 1.1 }}
                            transition={{ duration: 0.5 }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-gray-200/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                          {/* Category Badge - Light Gray */}
                          <div className="absolute top-4 left-4">
                            <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-gray-600 text-xs font-semibold rounded-full shadow-md border border-gray-200">
                              {blog.category || "General"}
                            </span>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="p-6 flex-1 flex flex-col">
                          {/* Meta Info */}
                          <div className="flex items-center justify-between mb-3 text-xs text-gray-500">
                            <div className="flex items-center space-x-2">
                              <HiOutlineCalendar className="w-4 h-4 text-gray-400" />
                              <span>{formatDate(blog.createdAt)}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <HiOutlineClock className="w-4 h-4 text-gray-400" />
                              <span>{getReadingTime(blog.article)}</span>
                            </div>
                          </div>

                          {/* Title */}
                          <h3 className="text-lg font-bold mb-3 text-gray-700 line-clamp-2 group-hover:text-gray-900 transition-colors duration-200">
                            {blog.title}
                          </h3>

                          {/* Description - Truncated */}
                          <p className="text-gray-600 mb-4 line-clamp-3 text-sm flex-1">
                            {blog.article.substring(0, 150)}...
                          </p>

                          {/* College Name and Read More */}
                          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                            <div className="flex items-center space-x-2">
                              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                                <HiOutlineOfficeBuilding className="w-4 h-4 text-gray-600" />
                              </div>
                              <span className="text-xs text-gray-600 font-medium truncate max-w-[120px]">
                                {collegeName}
                              </span>
                            </div>

                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <button
                                onClick={(e) => handleReadMore(blog, e)}
                                className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-700 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-all duration-200"
                              >
                                <span>Read More</span>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                            </motion.div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Load More Button - Gray */}
              {hasMore && (
                <motion.div
                  variants={itemVariants}
                  className="text-center mt-12"
                >
                  <motion.button
                    onClick={loadMore}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-8 py-3 bg-gray-700 text-white font-semibold rounded-xl shadow-lg hover:bg-gray-800 transition-all duration-200"
                  >
                    Load More Articles
                  </motion.button>
                </motion.div>
              )}

              {/* Results Count */}
              <motion.div
                variants={itemVariants}
                className="text-center text-gray-500 text-sm mt-6"
              >
                Showing {displayedBlogs.length} of {filteredBlogs.length} articles
              </motion.div>
            </>
          ) : (
            <motion.div
              variants={itemVariants}
              className="text-center p-12 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg max-w-md mx-auto border border-gray-200"
            >
              <div className="w-20 h-20 mx-auto bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4">
                <HiOutlineNewspaper className="w-10 h-10 text-gray-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-700 mb-2">No Blogs Found</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm ? "Try adjusting your search or filter" : "Check back later for new articles"}
              </p>
              {(searchTerm || selectedCategory !== "all") && (
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedCategory("all");
                  }}
                  className="px-6 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition"
                >
                  Clear Filters
                </button>
              )}
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* Blog Detail Modal */}
      <AnimatePresence>
        {isModalOpen && selectedBlog && (
          <motion.div
            variants={modalOverlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 sm:p-8 overflow-y-auto"
            onClick={closeModal}
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
          >
            {/* Modal Content */}
            <motion.div
              variants={modalContentVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
            >
              {/* Close Button */}
              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={closeModal}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-600 hover:text-gray-900 shadow-lg border border-gray-200"
              >
                <HiOutlineX size={20} />
              </motion.button>

              {/* Scrollable Content */}
              <div className="overflow-y-auto flex-1">
                {/* Hero Image */}
                <div className="relative h-64 md:h-96 overflow-hidden">
                  <motion.img
                    initial={{ scale: 1.2 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.8 }}
                    src={selectedBlog.image || "assets/default-blog.png"}
                    alt={selectedBlog.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  
                  {/* Category Badge */}
                  <motion.div
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="absolute top-6 left-6"
                  >
                    <span className="px-4 py-2 bg-amber-500 text-white text-sm font-semibold rounded-full shadow-lg">
                      {selectedBlog.category || "General"}
                    </span>
                  </motion.div>

                  {/* Title Overlay */}
                  <div className="absolute bottom-6 left-6 right-6">
                    <motion.h2
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.4 }}
                      className="text-2xl md:text-4xl font-bold text-white mb-2 drop-shadow-lg"
                    >
                      {selectedBlog.title}
                    </motion.h2>
                    
                    {/* Meta Info */}
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.5 }}
                      className="flex flex-wrap items-center gap-4 text-white/90 text-sm"
                    >
                      <div className="flex items-center space-x-2">
                        <HiOutlineCalendar className="w-4 h-4" />
                        <span>{formatDate(selectedBlog.createdAt)}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <HiOutlineClock className="w-4 h-4" />
                        <span>{getReadingTime(selectedBlog.article)}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <HiOutlineOfficeBuilding className="w-4 h-4" />
                        <span>{selectedBlog.adminId?.collegeName || "Unknown College"}</span>
                      </div>
                    </motion.div>
                  </div>
                </div>

                {/* Article Content */}
                <div className="p-6 md:p-10">
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="prose prose-lg max-w-none"
                  >
                    {/* Author Info */}
                    <div className="flex items-center justify-between mb-8 pb-8 border-b border-gray-200">
                      <div className="flex items-center space-x-4">
                        <div className="w-14 h-14 bg-gradient-to-r from-gray-600 to-gray-700 rounded-full flex items-center justify-center">
                          <HiOutlineUser className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">Written by</p>
                          <p className="text-gray-600">{selectedBlog.adminId?.collegeName || "Admin"}</p>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex space-x-2">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="p-2 bg-gray-100 rounded-lg text-gray-600 hover:bg-gray-200 transition"
                        >
                          <HiOutlineBookmark size={20} />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="p-2 bg-gray-100 rounded-lg text-gray-600 hover:bg-gray-200 transition"
                        >
                          <HiOutlineShare size={20} />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="p-2 bg-gray-100 rounded-lg text-gray-600 hover:bg-gray-200 transition"
                        >
                          <HiOutlinePrinter size={20} />
                        </motion.button>
                      </div>
                    </div>

                    {/* Full Article Text */}
                    <div className="text-gray-700 leading-relaxed space-y-4">
                      {selectedBlog.article.split('\n').map((paragraph, index) => (
                        <motion.p
                          key={index}
                          initial={{ y: 10, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{ delay: 0.7 + (index * 0.1) }}
                          className="text-base md:text-lg"
                        >
                          {paragraph}
                        </motion.p>
                      ))}
                    </div>
                  </motion.div>

                  {/* Related Articles */}
                  {relatedBlogs.length > 0 && (
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 1 }}
                      className="mt-12 pt-8 border-t border-gray-200"
                    >
                      <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                        <HiOutlineBookOpen className="w-6 h-6 mr-2 text-gray-600" />
                        Related Articles
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {relatedBlogs.map((related, index) => (
                          <motion.div
                            key={related._id}
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 1.1 + (index * 0.1) }}
                            whileHover={{ y: -4 }}
                            className="bg-gray-50 rounded-xl overflow-hidden border border-gray-200 cursor-pointer group"
                            onClick={() => setSelectedBlog(related)}
                          >
                            <div className="h-32 overflow-hidden">
                              <img
                                src={related.image || "assets/default-blog.png"}
                                alt={related.title}
                                className="w-full h-full object-cover group-hover:scale-110 transition duration-300"
                              />
                            </div>
                            <div className="p-3">
                              <h4 className="font-semibold text-gray-700 text-sm line-clamp-2 mb-2">
                                {related.title}
                              </h4>
                              <p className="text-xs text-gray-500">
                                {formatDate(related.createdAt)}
                              </p>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>

              {/* Bottom Navigation */}
              <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                <motion.button
                  whileHover={{ x: -4 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={closeModal}
                  className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition"
                >
                  <HiOutlineChevronLeft size={18} />
                  <span>Back to Blogs</span>
                </motion.button>
                
                <div className="flex space-x-2">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition text-sm"
                  >
                    Previous
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition text-sm"
                  >
                    Next
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Blog;